import React, { useCallback, useContext, useEffect, useState } from "react";

import cx from "classnames";
import { noop } from "lodash";
import { type PopupProps, Popup as UIPopup } from "semantic-ui-react";

import { ScrollContext } from "~/layout/ScrollContext";

import styles from "./css/Popup.module.css";

type Props = PopupProps & {
	onOpenChange?: (open: boolean) => void;
};

/**
 * This is just a wrapper on the Semantic UI Popup component to overwrite the `hideOnScroll` behaviour
 * @deprecated Use ~/ui/Tooltip instead.
 */
function Popup({ onOpenChange, children, hideOnScroll = !window.Cypress, className, ...rest }: Props) {
	const scrollContainer = useContext(ScrollContext);
	const [active, setActive] = useState(false);

	const open = useCallback(() => {
		onOpenChange?.(true);
		setActive(true);
	}, [onOpenChange]);

	const close = useCallback(
		function close() {
			onOpenChange?.(false);
			setActive(false);
		},
		[onOpenChange],
	);

	useEffect(() => {
		if (scrollContainer && hideOnScroll) {
			scrollContainer.addEventListener("scroll", close);
			return () => scrollContainer.removeEventListener("scroll", close);
		}
		return noop;
	}, [close, hideOnScroll, scrollContainer]);

	return (
		<UIPopup open={active} onOpen={open} onClose={close} className={cx(className, styles.popup)} {...rest}>
			{children}
		</UIPopup>
	);
}

function PopupTitle({ children }: React.PropsWithChildren<unknown>) {
	return <div className={styles.popupTitle}>{children}</div>;
}
function PopupContent({ children }: React.PropsWithChildren<unknown>) {
	return <div className={styles.popupContent}>{children}</div>;
}

Popup.Title = PopupTitle;
Popup.Content = PopupContent;

Popup.displayName = "Popup";

export default Popup;
